import { uiText } from "@/lib/i18n/uiText";
import { useParams, Link } from "@/lib/router-compat";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LegalDisclaimer } from "@/components/shared/LegalDisclaimer";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { useSEO } from "@/lib/useSEO";
import { breadcrumbJsonLd, articleJsonLd, organizationJsonLd, faqPageJsonLd } from "@/lib/seoSchema";
import { findBlogPost, BLOG_POSTS } from "@/lib/blog";
import { useLocalizedBlogPost } from "@/hooks/useLocalizedBlogPost";
import { currentUiLocale } from "@/lib/i18n/uiText";

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(currentUiLocale(), { day: "numeric", month: "long", year: "numeric" });

export default function BlogPost() {
  const { slug = "" } = useParams();
  const post = useLocalizedBlogPost(findBlogPost(slug));


  useSEO({
    title: post ? `${post.title} | Statlign Blog` : "Article Not Found | Statlign Blog",
    description: post?.description ?? "This article could not be found.",
    canonicalPath: `/blog/${slug}`,
    ogType: "article",
    keywords: post?.tags.join(", ")!,
    publishedTime: post?.date!,
    jsonLd: post
      ? [
          organizationJsonLd(),
          // Emit as NewsArticle so Google News + Top Stories can consider the
          // post. NewsArticle inherits every field Article defines, so this is
          // a safe rename of `@type` on top of `articleJsonLd`.
          { ...articleJsonLd({
            headline: post.title,
            description: post.description,
            path: `/blog/${post.slug}`,
            datePublished: post.date,
            section: "Blog",
            keywords: post.tags.join(", "),
          }), "@type": "NewsArticle" },
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
          ...(post.faqs?.length ? [faqPageJsonLd(post.faqs)] : []),
        ]
      : undefined!,
  });

  if (!post) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 py-12 text-center">
        <h1 className="text-2xl font-bold">{uiText("Article not found")}</h1>
        <p className="text-muted-foreground">
           {uiText("The article you're looking for doesn't exist.")}{" "}
          <Link to="/blog" className="text-primary hover:underline">
             {uiText("Back to the blog")} </Link>
          .
        </p>
      </div>
    );
  }

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Breadcrumbs
        items={[
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: post.title, path: `/blog/${post.slug}` },
        ]}
      />

      <header className="space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {post.tags.map((t) => (
            <Badge key={t} variant="secondary" className="text-xs">
              {t}
            </Badge>
          ))}
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{uiText(post.title)}</h1>
        <p className="text-sm text-muted-foreground">
          {formatDate(post.date)} · {post.author}
        </p>
        <p className="text-lg text-muted-foreground">{post.intro}</p>
      </header>

      <Card>
        <CardContent className="space-y-6 py-6">
          {post.sections.map((section, i) => (
            <section key={i} className="space-y-2">
              {section.heading && (
                <h2 className="text-lg font-semibold text-foreground">{uiText(section.heading)}</h2>
              )}
              {section.paragraphs.map((p, j) => (
                <p key={j} className="text-sm leading-relaxed text-muted-foreground">
                  {p}
                </p>
              ))}
            </section>
          ))}
          {post.faqs && post.faqs.length > 0 && (
            <section className="space-y-3 border-t pt-6">
              <h2 className="text-lg font-semibold text-foreground">{uiText("Frequently asked questions")}</h2>
              {post.faqs.map((f) => (
                <div key={f.question} className="space-y-1">
                  <h3 className="text-sm font-semibold text-foreground">{uiText(f.question)}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{uiText(f.answer)}</p>
                </div>
              ))}
            </section>
          )}
        </CardContent>
      </Card>

      {related.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground">{uiText("More from the blog")}</h2>
          <ul className="space-y-1">
            {related.map((r) => (
              <li key={r.slug}>
                <Link to={`/blog/${r.slug}`} className="text-sm text-primary hover:underline">
                  {uiText(r.title)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      <LegalDisclaimer />
    </div>
  );
}
